import { GameState } from '../game/GameState';
import { STRINGS } from './strings.zh-HK';

export interface FlowData {
  total: number;
  best: number;
}

/**
 * The start ("Ready") and end ("GameOver") cards.
 *
 * Driven by `sync(state, data)` every frame from `App` — it is a VIEW of the
 * state machine, not a separate state owner, so the two can never disagree.
 * DOM, so the title stays crisp and the button is a real, accessible control.
 * Mounted under `#hud`, pointer-transparent except for its one button.
 */
export class FlowOverlay {
  private readonly root: HTMLDivElement;
  private current: 'ready' | 'over' | null = null;

  constructor(parent: HTMLElement, private readonly onBegin: () => void) {
    this.root = document.createElement('div');
    this.root.className = 'flow-overlay';
    this.root.hidden = true;
    parent.appendChild(this.root);
  }

  sync(state: GameState, data: FlowData): void {
    const want = state === GameState.Ready ? 'ready' : state === GameState.GameOver ? 'over' : null;
    if (want === this.current) return;
    this.current = want;
    if (want === 'ready') this.showReady(data);
    else if (want === 'over') this.showOver(data);
    else this.hide();
  }

  private showReady(data: FlowData): void {
    this.root.replaceChildren(
      FlowOverlay.heading(STRINGS.title, STRINGS.subtitle),
      FlowOverlay.line(STRINGS.flow.readyBest, data.best),
    );
    this.root.appendChild(FlowOverlay.beginButton(STRINGS.controls.begin, this.onBegin));
    this.root.hidden = false;
  }

  private showOver(data: FlowData): void {
    this.root.replaceChildren(
      FlowOverlay.heading(STRINGS.state.gameOver, ''),
      FlowOverlay.line(STRINGS.flow.overTotal, data.total),
      FlowOverlay.line(STRINGS.flow.readyBest, data.best),
    );
    this.root.appendChild(FlowOverlay.beginButton(STRINGS.flow.again, this.onBegin));
    this.root.hidden = false;
  }

  private hide(): void {
    this.root.hidden = true;
    this.root.replaceChildren();
  }

  private static heading(title: string, subtitle: string): HTMLElement {
    const wrap = document.createElement('div');
    const t = document.createElement('div');
    t.className = 'flow-title';
    t.textContent = title;
    wrap.appendChild(t);
    if (subtitle) {
      const s = document.createElement('div');
      s.className = 'flow-subtitle';
      s.textContent = subtitle;
      wrap.appendChild(s);
    }
    return wrap;
  }

  private static line(label: string, value: number): HTMLElement {
    const p = document.createElement('p');
    p.className = 'flow-line';
    p.textContent = `${label} ${value}`;
    return p;
  }

  private static beginButton(label: string, onBegin: () => void): HTMLButtonElement {
    const button = document.createElement('button');
    button.type = 'button';
    button.className = 'control-button';
    button.textContent = label;
    button.setAttribute('aria-label', label);
    button.addEventListener('click', () => onBegin());
    return button;
  }
}
